import { type ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { aiNotice, roleLabel } from "../lib/format";
import { navFor, navItemFor } from "../lib/nav";
import type { DemoApi, DemoRole } from "../lib/demoApi";
import { LiveCallCard } from "./LiveCallCard";
import { useSession } from "./session";

/** Voxowa's mark: the tick with its dot, in the brand's emerald. */
export function Mark({ size = 22 }: { size?: number }) {
  return (
    <svg width={Math.round(size * 1.11)} height={size} viewBox="4 2 58 52" aria-hidden="true">
      <path
        d="M8 34L23 49L46 20"
        fill="none"
        stroke="var(--brand)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="54.1" cy="9.9" r="6.5" fill="var(--brand)" />
    </svg>
  );
}

export function Brand() {
  return (
    <div className="brand" aria-label="Voxowa">
      <Mark />
      <span className="brand-word">voxowa</span>
    </div>
  );
}

export function DemoBanner() {
  const { api } = useSession();
  if (!api.demo) return null;
  const demo = api as DemoApi;
  const roles: [DemoRole, string][] = [
    ["owner", "Owner"],
    ["solo", "Team of one"],
    ["manager", "Manager"],
    ["caller", "Telecaller"],
    [
      "platform",
      "Platform admin",
    ],
  ];
  return (
    <div className="demo-banner">
      <strong>Demo mode</strong>
      <span className="muted">
        Made-up data; nothing is saved or sent anywhere.
      </span>
      <span className="row">
        See it as:
        {roles.map(([role, label]) => (
          <button
            key={role}
            className="small"
            onClick={() => {
              demo.signInAs(role);
              window.location.hash = "#/";
            }}
          >
            {label}
          </button>
        ))}
      </span>
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { api, status, signOut } = useSession();
  const [open, setOpen] = useState(false);
  // Call review (D-112) shows for reviewers, owners and admins.
  const [reviews, setReviews] = useState(false);
  useEffect(() => {
    void api.qaMe().then((me) => setReviews(me.can_review)).catch(() => setReviews(false));
  }, [api]);
  // The platform sees how much the dashboard is used (D-182): a visit when it opens and when it comes back into view.
  useEffect(() => {
    const note = () => void api.noteDashboardVisit().catch(() => {});
    const onShow = () => document.visibilityState === "visible" && note();
    note();
    document.addEventListener("visibilitychange", onShow);
    return () => document.removeEventListener("visibilitychange", onShow);
  }, [api]);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  const member = status!.member!;
  const role = member.role;
  const notice = aiNotice(member);

  const items = navFor({
    role,
    solo: member.member_limit === 1,
    addOn: Boolean(member.features?.coach || member.features?.autopilot),
    canReview: reviews,
  });
  const current = navItemFor(items, location.pathname);

  return (
    <>
      <DemoBanner />
      <div className="shell">
        {open && <div className="scrim" onClick={() => setOpen(false)} />}
        <aside className={`sidebar${open ? " open" : ""}`}>
          <Brand />
          <div className="org-name">{member.org_name}</div>
          <nav className="nav">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} className={item === current ? "active" : ""}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-foot">
            <div>
              <strong>{member.display_name}</strong>
            </div>
            <div className="muted">{roleLabel[role]}</div>
            <button className="link" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </aside>
        <div>
          <div className="topbar">
            <button
              className="small"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
            <Mark size={20} />
            <strong>{member.org_name}</strong>
          </div>
          {notice && (
            <div className={`ai-notice ${notice.tone}`} role="status">
              <span>{notice.text}</span>
              {notice.buy && <Link to="/usage">Buy minutes</Link>}
            </div>
          )}
          <main className="main">
            {current?.tabs && current.tabs.length > 1 && (
              <nav className="page-tabs" aria-label={current.label}>
                {current.tabs.map((t) => (
                  <NavLink key={t.to} to={t.to} className={({ isActive }) => (isActive ? "active" : "")}>
                    {t.label}
                  </NavLink>
                ))}
              </nav>
            )}
            {children}
          </main>
        </div>
      </div>
      <LiveCallCard memberId={member.member_id} />
    </>
  );
}
